import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import includes from 'lodash/includes';
import get from 'lodash/get';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import { supportUrl } from 'Config/lang';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import Drm from 'Components/Utility/Drm';
import DlcBadge from 'Components/Cards/DlcBadge';

function ProductMessages({ product }) {
  if (!product) return false;
  const currentDrm = ProductRequirements.CurrentDrm(product);

  const vrRequired = get(product, 'vr_support.vr_required', false);

  const status = [];
  if (product && product.features && product.features.includes('Early Access')) {
    status.push('early-access');
  }
  if (product && product.type === 'dlc') {
    status.push('dlc');
  }
  if (product && product.type === 'dlc' && currentDrm === 'steam') {
    status.push('steam-dlc');
  }
  if (product && product.ubisoft_ska) {
    status.push('ubisoft');
  }
  if (product && product.origin_ska) {
    status.push('origin');
  }
  if (product && product.epic_ska) {
    status.push('epic-keyless');
  }
  if (product && !product.epic_ska && currentDrm === 'epicgames') {
    status.push('epic-keys');
  }
  if (currentDrm === 'bethesda') {
    status.push('bethesda');
  }
  if (currentDrm === 'rockstar') {
    status.push('rockstar');
  }
  if (currentDrm === 'esonline') {
    status.push('esonline');
  }
  if (currentDrm === 'oculus') {
    status.push('oculus');
  }
  if (currentDrm === 'gog') {
    if (product.vaultnKeyless) {
      status.push('gog-keyless');
    } else {
      status.push('gog-key');
    }
  }
  if (product.slug === 'catlateral-damage') {
    status.push('vr-update');
  }
  if (includes(product.slug, 'minecoins')) {
    status.push('minecoins');
  }
  if (vrRequired) {
    status.push('vr-required');
  }
  if (status === '') return false;

  let multipleBanners = false;
  if (status.length > 1) multipleBanners = true;

  if (
    !includes(status, 'bethesda') &&
    !includes(status, 'dlc') &&
    !includes(status, 'steam-dlc') &&
    !includes(status, 'early-access') &&
    !includes(status, 'epic-keyless') &&
    !includes(status, 'epic-keys') &&
    !includes(status, 'esonline') &&
    !includes(status, 'gog-key') &&
    !includes(status, 'gog-keyless') &&
    !includes(status, 'oculus') &&
    !includes(status, 'origin') &&
    !includes(status, 'rockstar') &&
    !includes(status, 'ubisoft') &&
    !includes(status, 'vr-update') &&
    !includes(status, 'vr-required') &&
    !includes(status, 'minecoins')) {
    return false;
  }
  return (
    <div data-nosnippet className="page-section-margin tight-margin">
      {includes(status, 'early-access') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.earlyaccess.title"
                defaultMessage="Early Access."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.earlyaccess.description"
              defaultMessage="This {productType} is currently not finished and may or may not change significantly over the course of its development."
              values={{
                productType: product.type,
              }}
            />&nbsp;
            <a href="http://store.steampowered.com/earlyaccessfaq/">
              <FormattedMessage
                id="product-messages.earlyaccess.findoutlink"
                defaultMessage="Find out more"
              />
            </a>
          </div>
        </div>
      }

      {includes(status, 'steam-dlc') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="icon-container">
            <Drm drmType={currentDrm} />
          </div>

          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.steam.title"
                defaultMessage="This title is redeemed via Steam."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.steam.description"
              defaultMessage="Your key will require activation on Steam."
            />&nbsp;
            <a href={`${supportUrl()}/articles/201101161`} target="_blank" rel="noopener noreferrer">
              <FormattedMessage
                id="product-messages.steam.findoutlink"
                defaultMessage="Find out more"
              />
            </a>
          </div>
        </div>
      }

      {includes(status, 'ubisoft') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="icon-container">
            <Drm drmType={currentDrm} />
          </div>

          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.ubisoft.title"
                defaultMessage="This title is redeemed via Ubisoft Connect."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.ubisoft.description"
              defaultMessage="Please note that you will NOT receive a Ubisoft Connect key."
            />&nbsp;
            <a href={`${supportUrl()}/articles/360000066997`} target="_blank" rel="noopener noreferrer">
              <FormattedMessage
                id="product-messages.ubisoft.findoutlink"
                defaultMessage="Find out more"
              />
            </a>
          </div>
        </div>
      }

      {includes(status, 'bethesda') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="icon-container">
            <Drm drmType={currentDrm} />
          </div>

          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.bethesda.title"
                defaultMessage="This title is delivered as a Bethesda DRM Key."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.bethesda.description"
              defaultMessage="Your key will require activation on Bethesda.net."
            />&nbsp;
            <a href={`${supportUrl()}/articles/360001626178`} target="_blank" rel="noopener noreferrer">
              <FormattedMessage
                id="product-messages.bethesda.findoutlink"
                defaultMessage="Find out more"
              />
            </a>
          </div>
        </div>
      }

      {includes(status, 'rockstar') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="icon-container">
            <Drm drmType={currentDrm} />
          </div>

          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.rockstar.title"
                defaultMessage="This title is delivered as Rockstar Games Social Club DRM."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.rockstar.description"
              defaultMessage="Your key will require activation on the Rockstar Games launcher."
            />&nbsp;
            <a href={`${supportUrl()}/articles/214143285`} target="_blank" rel="noopener noreferrer">
              <FormattedMessage
                id="product-messages.rockstar.findoutlink"
                defaultMessage="Find out more"
              />
            </a>
          </div>
        </div>
      }

      {includes(status, 'esonline') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="icon-container">
            <Drm drmType={currentDrm} />
          </div>

          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.esonline.title"
                defaultMessage="This title is delivered as Elder Scrolls Online DRM."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.esonline.description"
              defaultMessage="Your key will require activation at www.ElderScrollsOnline.com."
            />&nbsp;
            <a href={`${supportUrl()}/articles/206052731`} target="_blank" rel="noopener noreferrer">
              <FormattedMessage
                id="product-messages.esonline.findoutlink"
                defaultMessage="Find out more"
              />
            </a>
          </div>
        </div>
      }

      {includes(status, 'oculus') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="icon-container">
            <Drm drmType={currentDrm} />
          </div>

          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.oculus.title"
                defaultMessage="This title is delivered as Meta DRM."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.oculus.description"
              defaultMessage="Your key will require activation at Meta."
            />&nbsp;
            <a href={`${supportUrl()}/articles/4412963223313`} target="_blank" rel="noopener noreferrer">
              <FormattedMessage
                id="product-messages.oculus.findoutlink"
                defaultMessage="Find out more"
              />
            </a>
          </div>
        </div>
      }

      {includes(status, 'gog-key') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="icon-container">
            <Drm drmType={currentDrm} />
          </div>

          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.gog.title"
                defaultMessage="This title is delivered as GOG DRM."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.gog.description"
              defaultMessage="Your key will require activation at www.gog.com."
            />&nbsp;
            <a href={`${supportUrl()}/articles/360004034038`} target="_blank" rel="noopener noreferrer">
              <FormattedMessage
                id="product-messages.gog.findoutlink"
                defaultMessage="Find out more"
              />
            </a>
          </div>
        </div>
      }

      {includes(status, 'gog-keyless') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="icon-container">
            <Drm drmType={currentDrm} />
          </div>

          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.gog.title"
                defaultMessage="This title is delivered as GOG DRM."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.gog-keyless.description"
              defaultMessage="Your game will activate directly on www.gog.com. You will not reveive a GOG key"
            />&nbsp;
            <a href={`${supportUrl()}/articles/16925718616594`} target="_blank" rel="noopener noreferrer">
              <FormattedMessage
                id="product-messages.gog.findoutlink"
                defaultMessage="Find out more"
              />
            </a>
          </div>
        </div>
      }

      {includes(status, 'origin') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="icon-container">
            <Drm drmType={currentDrm} />
          </div>

          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.origin.title"
                defaultMessage="This title is redeemed via EA Origin."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.origin.description"
              defaultMessage="Please note that you will NOT receive an Origin key."
            />&nbsp;
            <a href={`${supportUrl()}/articles/360003854337`} target="_blank" rel="noopener noreferrer">
              <FormattedMessage
                id="product-messages.origin.findoutlink"
                defaultMessage="Find out more"
              />
            </a>
          </div>
        </div>
      }

      {includes(status, 'epic-keyless') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="icon-container">
            <Drm drmType={currentDrm} />
          </div>

          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.epic-keyless.title"
                defaultMessage="This title is delivered as Epic DRM."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.epic-keyless.description"
              defaultMessage="This title is redeemed via Epic connection and you will NOT receive a key."
            />
          </div>
        </div>
      }

      {includes(status, 'epic-keys') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="icon-container">
            <Drm drmType={currentDrm} />
          </div>

          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.epic-keys.title"
                defaultMessage="This title is delivered as Epic DRM."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.epic-keys.description"
              defaultMessage="Your key will require activation on the Epic launcher."
            />&nbsp;
            <a href={`${supportUrl()}/articles/360001750957`} target="_blank" rel="noopener noreferrer">
              <FormattedMessage
                id="product-messages.epic-keys.findoutlink"
                defaultMessage="Find out more"
              />
            </a>
          </div>
        </div>
      }

      {includes(status, 'dlc') &&
        <div className={classNames('product-message-banner dlc', { 'multiple-banners': multipleBanners })}>
          <div className="icon-container">
            <DlcBadge size="large" />
          </div>

          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.dlc.title"
                defaultMessage="Downloadable Content."
              />
            </span>&nbsp;
            {product && product.base_game ?
              <FormattedMessage
                id="product-messages.dlc.description.parent"
                defaultMessage="This content requires {parent} on the same platform in order to play"
                values={{
                  parent: <Link to={`/${product.base_game.type}/${product.base_game.slug}`}>{product.base_game.name}</Link>,
                }}
              />
              :
              <FormattedMessage id="product-messages.dlc.description" defaultMessage="This content requires the base game on the same platform in order to play" />
            }
          </div>
        </div>
      }

      {includes(status, 'vr-update') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.vr-update.title"
                defaultMessage="Free VR DLC Available."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.vr-update.description"
              defaultMessage="A free update can be downloaded post-purchase to add VR support to this title."
            />&nbsp;
            <a href="https://store.steampowered.com/app/474720/Catlateral_Damage_VR/" target="_blank" rel="noopener noreferrer">
              <FormattedMessage
                id="product-messages.vr-update.findoutlink"
                defaultMessage="Find out more"
              />
            </a>
          </div>
        </div>
      }

      {includes(status, 'vr-required') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.vr-required.title"
                defaultMessage="VR Required."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.vr-required.description"
              defaultMessage="This game requires a VR headset in order to play. See VR Support for more details."
            />
          </div>
        </div>
      }

      {includes(status, 'minecoins') &&
        <div className={classNames('product-message-banner', { 'multiple-banners': multipleBanners })}>
          <div className="message-container">
            <span className="message-title">
              <FormattedMessage
                id="product-messages.minecoins.title"
                defaultMessage="Multi-Platform."
              />
            </span>&nbsp;
            <FormattedMessage
              id="product-messages.minecoins.description"
              defaultMessage="You can redeem Minecoins on one of the following platforms - Android, iOS, Nintendo Switch, Windows or Xbox ONE."
            />
          </div>
        </div>
      }
    </div>
  );
}

ProductMessages.propTypes = {
  product: PropTypes.shape({}),
};

ProductMessages.defaultProps = {
  product: {},
};

export default (ProductMessages);
